import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Chip,
  MenuItem,
  Divider,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../api/axios';
import { findDuplicateQuestionPairs, flagPersonalDataQuestions } from '../utils/taskAssessmentIacAudit';

const KIND_LABELS = {
  mcq: 'MCQ',
  free_text: 'Free text',
  true_false: 'True/False',
  case_study: 'Case study'
};

/**
 * @param {string} props.kind
 * @param {object} props.payload
 * @param {function} props.onChange
 * @param {number} [props.qualificationId]
 * @param {number} [props.assessmentId]
 */
export default function AssessmentPayloadEditor({
  kind,
  payload,
  onChange,
  qualificationId,
  assessmentId
}) {
  const questions = Array.isArray(payload?.questions) ? payload.questions : [];
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);

  const duplicatePairs = useMemo(() => findDuplicateQuestionPairs(questions), [questions]);
  const personalFlags = useMemo(
    () => (kind === 'free_text' || kind === 'case_study' ? flagPersonalDataQuestions(questions) : []),
    [kind, questions]
  );

  const setQuestions = (nextQs) => {
    onChange({
      ...payload,
      questions: nextQs,
      metadata: payload?.metadata || {}
    });
  };

  const updateQuestion = (idx, patch) => {
    const next = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    setQuestions(next);
  };

  const deleteQuestion = (idx) => {
    if (!window.confirm('Delete this question?')) return;
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const regenerateQuestion = useCallback(
    async (idx) => {
      if (!qualificationId || !assessmentId) {
        alert('Save the assessment first before regenerating individual questions.');
        return;
      }
      setRegeneratingIdx(idx);
      try {
        const res = await api.post(
          `/qualifications/${qualificationId}/learning-content/task-assessments/${assessmentId}/regenerate-question`,
          { question_index: idx }
        );
        const q = res.data?.question;
        if (q) {
          const next = questions.map((item, i) => (i === idx ? q : item));
          setQuestions(next);
        }
      } catch (e) {
        alert(e.response?.data?.message || e.message || 'Regenerate failed');
      } finally {
        setRegeneratingIdx(null);
      }
    },
    [qualificationId, assessmentId, questions, setQuestions]
  );

  const duplicateIndices = useMemo(() => {
    const s = new Set();
    for (const p of duplicatePairs) {
      s.add(p.indexA);
      s.add(p.indexB);
    }
    return s;
  }, [duplicatePairs]);

  const renderToolbar = (idx) => (
    <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
      <Typography variant="subtitle2" sx={{ flex: 1 }}>
        Question {idx + 1}
        {duplicateIndices.has(idx) && (
          <Chip size="small" color="warning" label="Possible duplicate" sx={{ ml: 1 }} />
        )}
      </Typography>
      <IconButton size="small" color="error" onClick={() => deleteQuestion(idx)} title="Delete question">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      <Button
        size="small"
        variant="outlined"
        startIcon={regeneratingIdx === idx ? <CircularProgress size={14} /> : <RefreshIcon />}
        disabled={regeneratingIdx != null}
        onClick={() => regenerateQuestion(idx)}
      >
        Regenerate
      </Button>
    </Stack>
  );

  if (kind === 'mcq') {
    return (
      <Stack spacing={3}>
        {duplicatePairs.length > 0 && (
          <Alert severity="warning">
            {duplicatePairs.length} possible duplicate question pair(s) detected. Review or regenerate affected
            questions.
          </Alert>
        )}
        {questions.map((q, idx) => {
          const letters = q.options ? Object.keys(q.options).sort() : ['A', 'B', 'C', 'D'];
          return (
            <Paper key={q.id || idx} variant="outlined" sx={{ p: 2 }}>
              {renderToolbar(idx)}
              <TextField
                size="small"
                label="Task code"
                value={q.task_code || ''}
                onChange={(e) => updateQuestion(idx, { task_code: e.target.value })}
                sx={{ mb: 2, maxWidth: 360 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Question"
                value={q.question || ''}
                onChange={(e) => updateQuestion(idx, { question: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Options
              </Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {letters.map((L) => (
                  <TextField
                    key={L}
                    size="small"
                    label={`Option ${L}`}
                    value={(q.options && q.options[L]) || ''}
                    onChange={(e) => {
                      const opts = { ...(q.options || {}), [L]: e.target.value };
                      updateQuestion(idx, { options: opts });
                    }}
                  />
                ))}
              </Stack>
              <TextField
                select
                size="small"
                label="Correct answer"
                value={q.correct_answer || letters[0] || 'A'}
                onChange={(e) => updateQuestion(idx, { correct_answer: e.target.value })}
                sx={{ minWidth: 160, mb: 2 }}
              >
                {letters.map((L) => (
                  <MenuItem key={L} value={L}>
                    {L}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                label="IAC codes (comma-separated)"
                value={(Array.isArray(q.iac_codes) ? q.iac_codes : []).join(', ')}
                onChange={(e) =>
                  updateQuestion(idx, {
                    iac_codes: e.target.value
                      .split(/[,;]+/)
                      .map((s) => s.trim())
                      .filter(Boolean)
                  })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                type="number"
                size="small"
                label="Marks"
                value={q.marks ?? ''}
                onChange={(e) =>
                  updateQuestion(idx, { marks: parseInt(e.target.value, 10) || 0 })
                }
                sx={{ width: 120 }}
              />
            </Paper>
          );
        })}
      </Stack>
    );
  }

  if (kind === 'true_false') {
    return (
      <Stack spacing={3}>
        {questions.map((q, idx) => (
          <Paper key={q.id || idx} variant="outlined" sx={{ p: 2 }}>
            {renderToolbar(idx)}
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Statement"
              value={q.question || ''}
              onChange={(e) => updateQuestion(idx, { question: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              size="small"
              label="Correct answer"
              value={q.correct_answer === true ? 'true' : 'false'}
              onChange={(e) => updateQuestion(idx, { correct_answer: e.target.value === 'true' })}
              sx={{ minWidth: 160, mb: 2 }}
            >
              <MenuItem value="true">True</MenuItem>
              <MenuItem value="false">False</MenuItem>
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Explanation"
              value={q.explanation || ''}
              onChange={(e) => updateQuestion(idx, { explanation: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="IAC codes (comma-separated)"
              value={(Array.isArray(q.iac_codes) ? q.iac_codes : []).join(', ')}
              onChange={(e) =>
                updateQuestion(idx, {
                  iac_codes: e.target.value
                    .split(/[,;]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              type="number"
              size="small"
              label="Marks"
              value={q.marks ?? ''}
              onChange={(e) =>
                updateQuestion(idx, { marks: parseInt(e.target.value, 10) || 0 })
              }
              sx={{ width: 120 }}
            />
          </Paper>
        ))}
      </Stack>
    );
  }

  const isCaseStudy = kind === 'case_study';

  return (
    <Stack spacing={3}>
      {personalFlags.length > 0 && (
        <Alert severity="warning">
          {personalFlags.length} question(s) may ask for personal information. Use fictional case studies instead.
        </Alert>
      )}
      {duplicatePairs.length > 0 && (
        <Alert severity="warning">
          {duplicatePairs.length} possible duplicate question pair(s) detected.
        </Alert>
      )}
      {questions.map((q, idx) => (
        <Paper key={q.id || idx} variant="outlined" sx={{ p: 2 }}>
          {renderToolbar(idx)}
          <TextField
            size="small"
            label="Task code"
            value={q.task_code || ''}
            onChange={(e) => updateQuestion(idx, { task_code: e.target.value })}
            sx={{ mb: 2, maxWidth: 360 }}
          />
          {isCaseStudy && (
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Scenario (fictional case study)"
              value={q.scenario || ''}
              onChange={(e) => updateQuestion(idx, { scenario: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Question"
            value={q.question || ''}
            onChange={(e) => updateQuestion(idx, { question: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Marking guidelines"
            value={q.marking_guidelines || ''}
            onChange={(e) => updateQuestion(idx, { marking_guidelines: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Model answer (full marks)"
            value={q.suggested_answer_framework || ''}
            onChange={(e) =>
              updateQuestion(idx, { suggested_answer_framework: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            type="number"
            size="small"
            label="Marks"
            value={q.marks ?? ''}
            onChange={(e) =>
              updateQuestion(idx, { marks: parseInt(e.target.value, 10) || 0 })
            }
            sx={{ width: 120, mb: 2 }}
          />
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            IACs (comma-separated)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={(Array.isArray(q.iac_codes) ? q.iac_codes : []).join(', ')}
            onChange={(e) =>
              updateQuestion(idx, {
                iac_codes: e.target.value
                  .split(/[,;]+/)
                  .map((s) => s.trim())
                  .filter(Boolean)
              })
            }
            sx={{ mt: 0.5 }}
          />
        </Paper>
      ))}
    </Stack>
  );
}

export { KIND_LABELS };
